import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { saasCreateApp, portalGetTenantMetrics } from '../lib/portalApi';
import { userAppsCol } from '../lib/portalData';
import { onSnapshot } from 'firebase/firestore';

export function AppsPage() {
  const { user } = useAuth();
  
  const [apps, setApps] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [bundleId, setBundleId] = useState('');
  const [packageName, setPackageName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastApiKey, setLastApiKey] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [tenantMetrics, setTenantMetrics] = useState<any>(null);

  useEffect(() => {
    if (!user) {
      setApps([]);
      return;
    }
    const unsub = onSnapshot(userAppsCol(user.uid), (snap) => {
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setApps(list);
    });
    
    // Fetch tenant metrics
    user.getIdToken().then(idToken => {
      portalGetTenantMetrics({ tenantId: user.uid, idToken })
        .then(data => setTenantMetrics(data.metrics))
        .catch(console.error);
    });

    return () => unsub();
  }, [user]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setError(null);
    setLastApiKey(null);
    setBusy(true);
    try {
      const token = await user.getIdToken();
      const resp = await saasCreateApp({
        name: name.trim(),
        bundleId: bundleId.trim() || undefined,
        packageName: packageName.trim() || undefined,
        idToken: token,
      });
      setName('');
      setBundleId('');
      setPackageName('');
      setLastApiKey(resp.apiKey);
      setShowCreateForm(false);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create app');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Header Section */}
      <div className="flex flex-wrap gap-md justify-between items-end mb-lg md:mb-xl">
        <div>
          <h2 className="font-headline-lg text-[22px] md:text-[32px] font-semibold text-on-surface">My Apps</h2>
          <p className="font-body-md text-[14px] md:text-[16px] text-on-surface-variant mt-1">Manage your product ecosystem and integration keys.</p>
        </div>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-primary text-on-primary font-label-md text-[14px] px-lg py-md rounded-lg flex items-center gap-sm hover:opacity-90 active:scale-95 transition-all shadow-sm"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          Create New App
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg mb-lg shadow-sm">
          <h3 className="font-headline-md text-[20px] font-semibold text-on-surface mb-md">New Project Details</h3>
          <form onSubmit={handleCreate} className="space-y-md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-md">
              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">App Name (*)</label>
                <input 
                  className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50" 
                  value={name} onChange={(e) => setName(e.target.value)} required disabled={busy} placeholder="e.g. Ebook Libre" 
                />
              </div>
              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Apple Bundle ID (Optional)</label>
                <input 
                  className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50" 
                  value={bundleId} onChange={(e) => setBundleId(e.target.value)} disabled={busy} placeholder="com.example.app" 
                />
              </div>
              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Android Package Name (Optional)</label>
                <input 
                  className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50" 
                  value={packageName} onChange={(e) => setPackageName(e.target.value)} disabled={busy} placeholder="com.example.app" 
                />
              </div>
            </div>
            {error && <p className="text-error text-sm">{error}</p>}
            <div className="flex justify-end gap-sm mt-md">
              <button type="button" onClick={() => setShowCreateForm(false)} className="px-4 py-2 text-on-surface-variant hover:bg-surface-container-low rounded-lg transition-colors font-label-md">Cancel</button>
              <button type="submit" disabled={busy} className="bg-primary text-on-primary px-4 py-2 rounded-lg transition-all hover:opacity-90 disabled:opacity-50 font-label-md">Create</button>
            </div>
          </form>
        </div>
      )}

      {lastApiKey && (
        <div className="bg-tertiary-fixed-dim/20 border border-tertiary-fixed rounded-xl p-md mb-lg">
          <p className="font-label-md text-tertiary font-bold mb-2">Successfully created! Your API Key:</p>
          <code className="block bg-inverse-surface text-surface px-3 py-2 rounded-lg font-mono text-sm mb-2">{lastApiKey}</code>
          <p className="font-body-sm text-[13px] text-on-surface-variant">Please save this API Key for your Flutter SDK configuration.</p>
        </div>
      )}

      {/* Bento-style App Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg">
        {apps.map((a) => (
          <Link key={a.id} to={`/apps/${a.id}`} className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg flex flex-col hover:border-primary transition-all duration-200 cursor-pointer group hover:-translate-y-1 hover:shadow-lg">
            <div className="flex justify-between items-start mb-md">
              <div className="w-12 h-12 rounded-xl bg-primary-container/20 flex items-center justify-center text-primary">
                <span className="material-symbols-outlined text-[28px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                  {a.name.toLowerCase().includes('book') ? 'auto_stories' : 'apps'}
                </span>
              </div>
            </div>
            <h3 className="font-headline-md text-[20px] font-semibold text-on-surface group-hover:text-primary transition-colors mb-1">{a.name}</h3>
            <p className="font-label-sm text-[12px] text-on-surface-variant font-mono mb-lg truncate">ID: {a.id}</p>
            
            <div className="space-y-md flex-1">
              <div className="p-sm bg-surface-container-low rounded-lg border border-outline-variant/30">
                <div className="flex items-center gap-xs mb-1">
                  <span className="material-symbols-outlined text-[14px] text-on-surface-variant">phone_iphone</span>
                  <span className="font-label-sm text-[12px] text-on-surface-variant">iOS Bundle ID</span>
                </div>
                {a.bundleId ? (
                  <p className="font-body-sm text-[13px] font-mono text-on-surface truncate">{a.bundleId}</p>
                ) : (
                  <p className="font-body-sm text-[13px] font-mono text-on-surface-variant italic">Not configured</p>
                )}
              </div>
              
              <div className="p-sm bg-surface-container-low rounded-lg border border-outline-variant/30">
                <div className="flex items-center gap-xs mb-1">
                  <span className="material-symbols-outlined text-[14px] text-on-surface-variant">android</span>
                  <span className="font-label-sm text-[12px] text-on-surface-variant">Android Package</span>
                </div>
                {a.packageName ? (
                  <p className="font-body-sm text-[13px] font-mono text-on-surface truncate">{a.packageName}</p>
                ) : (
                  <p className="font-body-sm text-[13px] font-mono text-on-surface-variant italic">Not configured</p>
                )}
              </div>
            </div>
            
            <div className="mt-lg pt-md border-t border-outline-variant flex justify-between items-center">
              <span className="font-label-sm text-[12px] text-on-surface-variant">Settings</span>
              <span className="material-symbols-outlined text-outline-variant group-hover:text-primary transition-colors">arrow_forward</span>
            </div>
          </Link>
        ))}

        {/* Empty State / Add New Card */}
        <div onClick={() => setShowCreateForm(true)} className="bg-surface-container-low border-2 border-dashed border-outline-variant rounded-xl p-lg flex flex-col items-center justify-center min-h-[340px] hover:bg-surface-container-high hover:border-primary transition-all group cursor-pointer">
          <div className="w-12 h-12 rounded-full border-2 border-outline-variant flex items-center justify-center text-outline-variant group-hover:border-primary group-hover:text-primary transition-colors mb-md">
            <span className="material-symbols-outlined text-[24px]">add</span>
          </div>
          <p className="font-headline-md text-[20px] font-semibold text-outline group-hover:text-primary transition-colors">Add New Project</p>
          <p className="font-body-sm text-[14px] text-on-surface-variant mt-sm text-center">Configure a new app to start managing subscriptions.</p>
        </div>
      </div>

      {/* Dashboard Stats Preview Section */}
      <section className="mt-xl grid grid-cols-1 lg:grid-cols-4 gap-md md:gap-lg mb-10">
        <div className="lg:col-span-1 bg-surface-container-lowest border border-outline-variant p-lg rounded-xl">
          <h4 className="font-label-md text-[14px] text-on-surface-variant mb-md">Total Revenue (MTD)</h4>
          <div className="flex items-baseline gap-sm">
            <p className="font-headline-md text-[24px] font-semibold text-on-surface">
              ${tenantMetrics?.mrr ? tenantMetrics.mrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
            </p>
            <span className="font-label-sm text-[12px] text-on-surface-variant flex items-center">tháng này</span>
          </div>
        </div>
        <div className="lg:col-span-3 bg-surface-container-lowest border border-outline-variant p-lg rounded-xl relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start mb-md">
            <div>
              <h4 className="font-label-md text-[14px] text-on-surface-variant">Global Active Users</h4>
              <p className="font-headline-md text-[24px] font-semibold text-on-surface">
                {tenantMetrics?.active?.all ? tenantMetrics.active.all.toLocaleString() : '0'} 
                <span className="font-label-sm text-[12px] text-outline ml-2 font-normal">Total across all apps</span>
              </p>
            </div>
          </div>
          <p className="font-body-sm text-[12px] text-on-surface-variant mt-2">
            Không tính giao dịch sandbox. Xem chi tiết theo từng app ở tab Dashboard của app đó.
          </p>
        </div>
      </section>
    </>
  );
}
